import { Box, Button, IconButton, Modal, Typography, styled } from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import StarIcon from '@mui/icons-material/Star';
import StarBorderIcon from '@mui/icons-material/StarBorder';
import { TextareaAutosize } from '@mui/material';
import { useState } from 'react';
import { Link } from '@mui/material';
import { ChevronRightOutlined } from '@mui/icons-material';
import feedbackIcon from 'assets/images/feedback-icon.png';
import { useMutation } from '@tanstack/react-query';
import axios from 'axios';
import { toast } from 'react-toastify';


const StyledTextArea = styled(TextareaAutosize)(() => ({
    width: '100%',
    minHeight: '100px',
    marginTop: '16px',
    padding: '12px',
    borderRadius: '8px',
    border: '1px solid var(--Light-Gray)',
    fontFamily: 'inherit',
    fontSize: '14px',
    lineHeight: '20px',
    resize: 'vertical',
    '&:focus': {
        outline: 'none',
    },
}));

interface FeedbackData {
    grade: number;
    comment: string;
    endpoint: string
}

interface LeaveFeedbackProps {
    open: boolean;
    endpoint: string;
    interviewId? : string;
    onClose: () => void;
    onSubmit: (data: FeedbackData) => void;
}

function LeaveFeedback({ open, endpoint, interviewId, onClose }: LeaveFeedbackProps) {
    const [rating, setRating] = useState(1);
    const [feedback, setFeedback] = useState('');

    const sendFeedback = useMutation({
        mutationFn: async (data: FeedbackData) => {
            const token = localStorage.getItem('token');
            const response = await axios.put(`${endpoint}/${interviewId}/feedback`, {
                grade : data.grade,
                comment: data.comment,
            },
        {
            headers: {
                Authorization: `Bearer ${token}`,
                "Content-Type": "application/json",
            },
        })
            return response.data
        },
        onSuccess: (data) => {
            toast.success('Thank you for your feedback!')
            setRating(1);
            setFeedback('');
            onClose();
        },
        onError: (error) => {
            console.log(error);
        }
    })

    const handleSubmit = () => {
        sendFeedback.mutate({
            grade: rating,
            comment: feedback,
            endpoint,

        })
    };

    return (
        <Modal
            open={open}
            onClose={onClose}
            aria-labelledby="modal-title"
            aria-describedby="modal-description"
        >
            <Box
                sx={{
                    position: 'absolute',
                    top: '50%',
                    left: '50%',
                    transform: 'translate(-50%, -50%)',
                    width: {
                        xs: 'calc(100% - 32px)', // Full width minus 16px padding on each side for mobile
                        sm: 'calc(100% - 48px)', // Slightly larger padding for tablets
                        md: '588px' // Original width for desktop
                    },
                    bgcolor: 'background.paper',
                    boxShadow: 24,
                    p: {
                        xs: '24px', // Smaller padding for mobile
                        sm: '32px', // Medium padding for tablets
                        md: '48px' // Original padding for desktop
                    },
                    borderRadius: '16px',
                    textAlign: 'center',
                }}
            >
                <IconButton
                    sx={{ position: 'absolute', top: 8, right: 8 }}
                    onClick={onClose}
                >
                    <CloseIcon />
                </IconButton>
                <img src={feedbackIcon} style={{width: '80px', height: '80px'}} alt="Feedback icon" />
                <Typography id="modal-title" variant="h1" component="h2" sx={{mt: '24px'}}>
                    Leave feedback
                </Typography>
                <Typography id="modal-description" sx={{mt: '8px'}}>
                    Give a rating to this interview and earn 1 free credit
                </Typography>
                <Box sx={{ display: 'flex', justifyContent: 'center', mt: '48px' }}>
                    {[...Array(5)].map((_, index) => (
                        <Box
                            key={index}
                            onClick={() => setRating(index + 1)}
                            sx={{ cursor: 'pointer' }}
                        >
                            {index < rating ? (
                                <StarIcon fontSize="large" sx={{ color: '#31C07D'}} />
                            ) : (
                                <StarBorderIcon fontSize="large" sx={{ color: '#31C07D' }} />
                            )}
                        </Box>
                    ))}
                </Box>
                <Typography sx={{mt: '32px', textAlign: 'left', color: 'black' }} variant='h4'>Additional feedback</Typography>
                <StyledTextArea 
                    placeholder="Type in your answer here..." 
                    value={feedback}
                    onChange={(e) => setFeedback(e.target.value)}
                />
                <Button 
                    onClick={handleSubmit} 
                    variant="contained" 
                    color="primary" 
                    sx={{ mt: '24px', width: '100%' }}
                    disabled={rating === 0}
                >
                    Submit
                </Button>
                <Box
                    sx={{
                        display: 'flex',
                        flexDirection: {
                            xs: 'column',
                            sm: 'row'
                        },
                        gap: '4px',
                        alignItems: 'center',
                        color: 'var(--Interviewer-Green)',
                        justifyContent: 'center',
                        mt: '32px',
                    }}>
                    <Typography 
                        variant="h4" 
                        sx={{ 
                            textAlign: 'center',
                            fontSize: {
                                xs: '16px',
                                sm: 'inherit'
                            }
                        }}
                    >
                        Are we missing a feature?
                    </Typography>
                    <Box sx={{
                        display: 'flex', 
                        alignItems: 'center',
                        '&:hover': {
                            opacity: 0.8,
                            cursor: 'pointer'
                        },
                    }}>
                        <Link
                            href="https://example.com"
                            target="_blank"
                            rel="noopener noreferrer"
                            sx={{
                                fontSize: {
                                    xs: '14px',
                                    sm: 'inherit'
                                }
                            }}
                        >
                            Request a feature
                        </Link>
                        <ChevronRightOutlined sx={{
                            fontSize: {
                                xs: '16px',
                                sm: 'inherit'
                            }
                        }} />
                    </Box>
                </Box>
            </Box>
        </Modal>
    );
}

export default LeaveFeedback;

